import { utils } from '@neo-one/utils';
import { AsyncIterableX } from '@reactivex/ix-esnext-esm/asynciterable/asynciterablex';
import { filter } from '@reactivex/ix-esnext-esm/asynciterable/pipe/filter';
import { map } from '@reactivex/ix-esnext-esm/asynciterable/pipe/map';
import * as argAssertions from '../args';
import { CannotSendFromContractError, CannotSendToContractError, HashArgumentExpectedError, NoContractDeployedError, TransferArgumentExpectedError, } from '../errors';
import { events as traceEvents } from '../trace';
import * as common from './common';
export const getParamAndOptionsResults = ({ parameters, args, send, completeSend, refundAssets, }) => {
    const hasRest = parameters.length > 0 && parameters[parameters.length - 1].rest;
    const hasForwardValueOptions = hasRest && parameters[parameters.length - 1].type === 'ForwardValue';
    return args.reduceRight((acc, right) => {
        if (hasForwardValueOptions && acc.forwardOptions === undefined && common.isForwardValueOptions(right)) {
            return {
                ...acc,
                forwardOptions: right,
            };
        }
        if (common.isTransactionOptions(right) && acc.options === undefined) {
            return {
                ...acc,
                options: right,
            };
        }
        if (acc.transfer === undefined && send) {
            try {
                const maybeTransfer = argAssertions.assertTransfer('transfer', right);
                return {
                    ...acc,
                    transfer: maybeTransfer,
                };
            }
            catch {
            }
        }
        if (acc.hash === undefined && (completeSend || refundAssets)) {
            try {
                const maybeHash = argAssertions.assertHash256('hash', right);
                return {
                    ...acc,
                    hash: maybeHash,
                };
            }
            catch {
            }
        }
        return {
            ...acc,
            requiredArgs: [right].concat(acc.requiredArgs),
        };
    }, {
        requiredArgs: [],
        options: undefined,
        forwardOptions: undefined,
        transfer: undefined,
        hash: undefined,
    });
};
export const getParamsAndOptions = ({ definition: { networks }, parameters, args, sendUnsafe, receive, send, completeSend, refundAssets, client, }) => {
    const { requiredArgs, forwardOptions, options: optionsIn, transfer, hash } = getParamAndOptionsResults({
        parameters,
        args,
        send,
        completeSend,
        refundAssets,
    });
    if (transfer === undefined && send) {
        throw new TransferArgumentExpectedError();
    }
    if (hash === undefined && (completeSend || refundAssets)) {
        throw new HashArgumentExpectedError();
    }
    const currentAccount = client.getCurrentUserAccount();
    const options = (optionsIn === undefined || optionsIn.from === undefined) && currentAccount !== undefined
        ? {
            ...optionsIn,
            from: currentAccount.id,
        }
        : {
            ...optionsIn,
        };
    const network = options.network === undefined
        ? options.from === undefined
            ? client.getCurrentNetwork()
            : options.from.network
        : options.network;
    const contractNetwork = networks[network];
    if (contractNetwork === undefined) {
        throw new NoContractDeployedError(network);
    }
    if (options.sendFrom !== undefined && !sendUnsafe) {
        throw new CannotSendFromContractError(contractNetwork.address);
    }
    if (options.sendTo !== undefined && !receive) {
        throw new CannotSendToContractError(contractNetwork.address);
    }
    const { converted, zipped } = common.convertParams({
        params: requiredArgs,
        parameters,
        senderAddress: currentAccount === undefined ? undefined : currentAccount.id.address,
    });
    return {
        params: converted,
        paramsZipped: zipped,
        options,
        forwardOptions: forwardOptions === undefined ? {} : forwardOptions,
        network,
        address: contractNetwork.address,
        transfer,
        hash,
    };
};
const createCall = ({ definition, client, func: { name, parameters = [], returnType }, }) => async (...args) => {
    const { params, network, address } = getParamsAndOptions({
        definition,
        parameters,
        args,
        sendUnsafe: false,
        receive: false,
        send: false,
        completeSend: false,
        refundAssets: false,
        client,
    });
    const receipt = await client.__call(network, address, name, params);
    return common.convertCallResult({
        returnType,
        result: receipt.result,
        actions: receipt.actions,
        sourceMaps: definition.sourceMaps,
    });
};
const createInvoke = ({ definition, client, func: { name, parameters = [], returnType, send = false, refundAssets = false, completeSend = false, sendUnsafe = false, receive = false, claim = false, }, }) => {
    const invoke = async (...args) => {
        const { params, paramsZipped, options, forwardOptions, address, transfer, hash } = getParamsAndOptions({
            definition,
            parameters,
            args,
            sendUnsafe,
            receive,
            send,
            completeSend,
            refundAssets,
            client,
        });
        if (claim) {
            return client.__invokeClaim(address, name, params, paramsZipped, options, definition.sourceMaps);
        }
        let result;
        if (send) {
            if (transfer === undefined) {
                throw new Error('Something went wrong.');
            }
            result = await client.__invokeSend(address, name, params, paramsZipped, transfer, options, definition.sourceMaps);
        }
        else if (refundAssets) {
            if (hash === undefined) {
                throw new Error('Something went wrong.');
            }
            result = await client.__invokeRefundAssets(address, name, params, paramsZipped, hash, options, definition.sourceMaps);
        }
        else if (completeSend) {
            if (hash === undefined) {
                throw new Error('Something went wrong.');
            }
            result = await client.__invokeCompleteSend(address, name, params, paramsZipped, hash, options, definition.sourceMaps);
        }
        else {
            result = await client.__invoke(address, name, params, paramsZipped, sendUnsafe || receive, options, definition.sourceMaps);
        }
        return {
            transaction: result.transaction,
            confirmed: async (getOptions) => {
                const receipt = await result.confirmed(getOptions);
                const { events = [] } = definition.abi;
                const { events: forwardEvents = [] } = forwardOptions;
                const actions = common.convertActions({
                    actions: receipt.actions,
                    events: events.concat(forwardEvents),
                });
                const invocationResult = await common.convertInvocationResult({
                    returnType,
                    result: receipt.result,
                    actions: receipt.actions,
                    sourceMaps: definition.sourceMaps,
                });
                return {
                    blockIndex: receipt.blockIndex,
                    blockHash: receipt.blockHash,
                    globalIndex: receipt.globalIndex,
                    transactionIndex: receipt.transactionIndex,
                    result: invocationResult,
                    events: common.filterEvents(actions),
                    logs: common.filterLogs(actions),
                    raw: receipt,
                };
            },
        };
    };
    invoke.confirmed = async (...args) => {
        const finalArg = args[args.length - 1];
        let options;
        if (common.isOptionsBase(finalArg)) {
            options = finalArg;
        }
        const result = await invoke(...args);
        const receipt = await result.confirmed(options);
        return { ...receipt, transaction: result.transaction };
    };
    return invoke;
};
export const createSmartContract = ({ definition, client, }) => {
    const { abi: { events: abiEvents = [] }, } = definition;
    const events = traceEvents.concat(abiEvents).reduce((acc, event) => ({
        ...acc,
        [event.name]: event,
    }), {});
    const iterActionsRaw = ({ network = client.getCurrentNetwork(), ...iterOptions } = {}) => AsyncIterableX.from(client.__iterActionsRaw(network, iterOptions)).pipe(filter((action) => action.address === definition.networks[network].address));
    const convertAction = (action) => {
        const converted = common.convertAction({ action, events });
        return typeof converted === 'string' ? undefined : converted;
    };
    const iterActions = (options) => AsyncIterableX.from(iterActionsRaw(options)).pipe(map(convertAction), filter(utils.notNull));
    const iterEvents = (options) => AsyncIterableX.from(iterActions(options)).pipe(map((action) => {
        if (action.type === 'Log') {
            return undefined;
        }
        return action;
    }), filter(utils.notNull), filter(Boolean));
    const iterLogs = (options) => AsyncIterableX.from(iterActions(options)).pipe(map((action) => {
        if (action.type === 'Event') {
            return undefined;
        }
        return action;
    }), filter(utils.notNull), filter(Boolean));
    return definition.abi.functions.reduce((acc, func) => common.addForward(func, abiEvents, {
        ...acc,
        [func.name]: func.constant === true
            ? createCall({
                definition,
                client,
                func,
            })
            : createInvoke({
                definition,
                client,
                func,
            }),
    }), {
        client,
        iterEvents,
        iterLogs,
        iterActions,
        convertAction,
        definition,
    });
};

//# sourceMappingURL=createSmartContract.js.map
